/**
 * Thẻ "Chuẩn bị tiệm · x/4" — hiện ở đầu màn Lịch cho tới khi tiệm nhận lịch được THẬT.
 *
 * Vì sao: tiệm mới đăng nhập lần đầu từng thấy **màn trắng** — không một câu nào nói phải làm gì
 * (vòng chấm UI 0). Kế hoạch 10/2026, 1.3: bản cũ tự suy từ 3 danh sách ("có thợ" = xong) nên báo xong khi chưa
 * ai nhận khách được, và biến mất trước bước QR. Nay máy chủ kiểm đúng điều kiện (`GET /v1/admin/chuan-bi-tiem`):
 * giờ mở cửa · dịch vụ và giá · người nhận khách (có ca thật) · bước cuối theo cờ Live (QR hoặc đặt thử một lịch).
 *
 * Chỉ ẩn khi đủ 4. Chỉ chủ tiệm / quản lý thấy (người gọi đã kiểm quyền `cau-hinh.tiem`).
 */
import { Check, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { tiem } from './api.ts'
import type { TheCauHinh } from './CauHinh.tsx'
import {
  type BuocChuanBi,
  buocChuanBi,
  type TinhTrangChuanBi,
  theChuanBiConHien,
} from './chuan-bi-tiem.ts'
import { daMoMaQrTrenMay } from './da-mo-ma-qr.ts'
import { E } from './text/mo-tiem.ts'
import { T } from './text/vi.ts'
import { ThongBao } from './ui.tsx'

/** Chữ + nơi mở của từng bước. */
function moTaBuoc(b: BuocChuanBi): { ten: string; goiY: string; the: TheCauHinh | 'dat-thu' } {
  switch (b.ma) {
    case 'gio-mo-cua':
      return {
        ten: T.setupStep1,
        goiY: b.xong ? T.setupStep1HintDone : T.setupStep1Hint,
        the: 'chi-nhanh',
      }
    case 'dich-vu':
      return {
        ten: T.setupStep2,
        goiY: b.giaMau ? T.setupStep2HintGiaMau : b.xong ? T.setupStep2HintSua : T.setupStep2Hint,
        the: 'dich-vu',
      }
    case 'nguoi-nhan-khach':
      return {
        ten: T.setupStep3,
        goiY: b.xong ? T.setupStep3HintDone : T.setupStep3Hint,
        the: 'nhan-vien',
      }
    case 'ma-qr':
      return {
        ten: T.setupStepQr,
        goiY: b.xong ? T.setupStepQrHintDone : T.setupStepQrHint,
        the: 'ma-qr',
      }
    case 'dat-thu':
      return {
        ten: T.setupStepThu,
        goiY: b.xong ? T.setupStepThuHintDone : T.setupStepThuHint,
        the: 'dat-thu',
      }
  }
}

export function ChuanBiTiem({
  maTiem,
  onMo,
  onDatThu,
  live,
  taiLai,
  chao = false,
  onDongChao,
  onHien,
}: {
  /** Mã tiệm của người đang đăng nhập — khoá cờ "đã mở mã QR" theo (tiệm, máy). */
  maTiem: string
  onMo: (the: TheCauHinh) => void
  /** Mở tờ đặt lịch (bước "Đặt thử một lịch"). `null` = chưa đặt được (chưa có chi nhánh / không có quyền). */
  onDatThu: (() => void) | null
  /** Ứng dụng đặt lịch trên Zalo đã chạy thật (`toi.miniAppLive`). */
  live: boolean
  /** Đổi giá trị này thì tải lại tình trạng (vd. vừa đặt xong một lịch). */
  taiLai: boolean
  /** Vừa mở tiệm xong: dòng đầu thẻ nhắc "lần sau đăng nhập bằng số điện thoại và mã bạn vừa đặt". */
  chao?: boolean
  /** Chủ bấm một bước hoặc đóng lời chào. */
  onDongChao?: () => void
  /** Báo cho màn Lịch biết thẻ có đang hiện không (để ẩn hộp "Chưa có lịch nào" cho khỏi lặp nút "Đặt lịch"). */
  onHien?: (hien: boolean) => void
}) {
  const [tt, setTt] = useState<TinhTrangChuanBi | null>(null)
  /** Đã có kết quả (thành công hay lỗi) — chưa tải xong thì chưa báo "không hiện" cho màn Lịch (khỏi nháy hộp rỗng). */
  const [daTai, setDaTai] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: `taiLai` chỉ để kích hoạt tải lại.
  useEffect(() => {
    void tiem
      .chuanBi()
      .then(setTt)
      // Thẻ này là lời nhắc, không phải chức năng: lỗi ở đây không được làm hỏng màn lịch.
      .catch(() => setTt(null))
      .finally(() => setDaTai(true))
  }, [taiLai])

  // Đọc lại mỗi lần vẽ: chủ mở tờ QR ở Cấu hình rồi quay về Lịch thì màn Lịch dựng lại thẻ (đọc cờ localStorage rẻ).
  const daMoQr = daMoMaQrTrenMay(maTiem)
  const hien = tt !== null && theChuanBiConHien(tt, live, daMoQr)
  useEffect(() => {
    if (daTai) onHien?.(hien)
  }, [daTai, hien, onHien])

  if (tt === null || !hien) return null
  const buoc = buocChuanBi(tt, live, daMoQr)
  const soXong = buoc.filter((b) => b.xong).length
  const tiepTheo = buoc.find((b) => !b.xong)?.ma

  return (
    <section className="the chuan-bi" aria-label={T.setupTitle}>
      {chao && (
        <div className="chuan-bi-chao">
          <ThongBao
            loai="thanh-cong"
            lon
            {...(onDongChao === undefined ? {} : { onDong: onDongChao })}
          >
            {E.daMoXong}
          </ThongBao>
        </div>
      )}
      <div>
        <h2>{T.setupTitle}</h2>
        <p className="chu-phu" style={{ marginTop: 6 }}>
          {live ? T.setupHintLive : T.setupHint}
        </p>
        <div className="tien-do" aria-hidden="true">
          <i style={{ width: `${(soXong / buoc.length) * 100}%` }} />
        </div>
        <p className="chu-phu" style={{ marginTop: 8 }}>
          {T.setupProgress(soXong, buoc.length)}
        </p>
      </div>
      <div>
        {buoc.map((b, i) => {
          const mo = moTaBuoc(b)
          return (
            <button
              key={b.ma}
              type="button"
              className={`buoc-chuan-bi ${b.xong ? 'xong' : b.ma === tiepTheo ? 'tiep-theo' : ''}`}
              onClick={() => {
                onDongChao?.()
                // "Đặt thử một lịch" cần chi nhánh để mở tờ đặt lịch — chưa có thì dẫn sang dựng chi nhánh.
                if (mo.the !== 'dat-thu') onMo(mo.the)
                else if (onDatThu === null) onMo('chi-nhanh')
                else onDatThu()
              }}
            >
              <span className="dau">{b.xong ? <Check size={18} /> : i + 1}</span>
              <div>
                <b>{mo.ten}</b>
                <span>{mo.goiY}</span>
              </div>
              <ChevronRight size={20} />
            </button>
          )
        })}
      </div>
    </section>
  )
}
